import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Mic,
  MicOff,
  Phone,
  PhoneOff,
  Grid3X3,
  Voicemail,
} from "lucide-react";
import { getCallConfig } from "@/api/call-config";
import { cn } from "@/cn";
import { useSoftphone } from "./softphone-context";

export function SoftphoneDock() {
  const sp = useSoftphone();
  const [notes, setNotes] = useState("");
  const [purposeId, setPurposeId] = useState("");
  const [dispositionId, setDispositionId] = useState("");
  const [showPad, setShowPad] = useState(false);
  const [vmOpen, setVmOpen] = useState(false);

  const configQuery = useQuery({
    queryKey: ["call-config"],
    queryFn: getCallConfig,
    staleTime: 30_000,
  });
  const config = configQuery.data?.config;

  if (
    sp.status === "idle" &&
    !sp.showPostCall &&
    !sp.activeCall &&
    !sp.error &&
    !sp.dialSession
  ) {
    return null;
  }

  const session = sp.dialSession;
  const sessionProgress =
    session != null
      ? `${Math.min(session.currentIndex + 1, session.leadIds.length)}/${session.leadIds.length}`
      : null;

  return (
    <div className="pointer-events-none fixed inset-x-0 bottom-[calc(3.5rem+env(safe-area-inset-bottom))] z-50 flex justify-center p-3 md:bottom-0 md:p-4">
      <div className="pointer-events-auto w-full max-w-xl rounded-xl border border-border bg-surface shadow-lg">
        {session && (
          <div className="flex flex-wrap items-center gap-2 border-b border-border px-4 py-2 text-xs">
            <span className="font-medium capitalize">
              {session.type} dial {sessionProgress}
            </span>
            <span className="text-muted-foreground">
              {session.targets?.[session.currentIndex]?.leadName ||
                session.targets?.[session.currentIndex]?.to ||
                "Next contact"}
            </span>
            <div className="ml-auto flex gap-2">
              <button
                type="button"
                className="rounded border border-border px-2 py-0.5"
                onClick={() => void sp.skipDialSessionTarget()}
              >
                Skip
              </button>
              <button
                type="button"
                className="rounded border border-border px-2 py-0.5 text-red-600"
                onClick={() => void sp.cancelActiveDialSession()}
              >
                End session
              </button>
            </div>
          </div>
        )}
        {(sp.status === "in_call" ||
          sp.status === "ringing" ||
          sp.status === "connecting" ||
          sp.status === "ready" ||
          sp.status === "error") &&
          !sp.showPostCall && (
            <div className="flex flex-wrap items-center gap-2 px-4 py-3">
              <Phone className="size-4 text-brand" />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">
                  {sp.activeCall?.leadName ||
                    sp.activeCall?.toNumber ||
                    (sp.twilioConfigured ? "Dialer ready" : "Dialer (log only)")}
                </p>
                <p className="text-xs text-muted-foreground">
                  {sp.status.replace("_", " ")}
                  {!sp.twilioConfigured && " · Twilio not configured"}
                </p>
              </div>
              {(sp.status === "in_call" || sp.status === "ringing") && (
                <>
                  <button
                    type="button"
                    onClick={sp.toggleMute}
                    className="rounded-md border border-border p-2 hover:bg-surface-hover"
                    title={sp.muted ? "Unmute" : "Mute"}
                  >
                    {sp.muted ? (
                      <MicOff className="size-4" />
                    ) : (
                      <Mic className="size-4" />
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowPad((v) => !v)}
                    className="rounded-md border border-border p-2 hover:bg-surface-hover"
                    title="Keypad"
                  >
                    <Grid3X3 className="size-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setVmOpen((v) => !v)}
                    className="rounded-md border border-border p-2 hover:bg-surface-hover"
                    title="Voicemail drop"
                  >
                    <Voicemail className="size-4" />
                  </button>
                  <button
                    type="button"
                    onClick={sp.hangup}
                    className="rounded-md bg-red-600 p-2 text-white hover:bg-red-700"
                    title="Hang up"
                  >
                    <PhoneOff className="size-4" />
                  </button>
                </>
              )}
              {(sp.status === "ready" || sp.status === "error") && (
                <button
                  type="button"
                  onClick={() => void sp.connectDevice()}
                  className="rounded-md border border-border px-2 py-1 text-xs"
                >
                  Reconnect
                </button>
              )}
            </div>
          )}

        {showPad && sp.status === "in_call" && (
          <div className="grid grid-cols-3 gap-1 border-t border-border px-4 py-2">
            {"123456789*0#".split("").map((d) => (
              <button
                key={d}
                type="button"
                className="rounded-md border border-border py-2 text-sm hover:bg-surface-hover"
                onClick={() => sp.sendDigits(d)}
              >
                {d}
              </button>
            ))}
          </div>
        )}

        {vmOpen && config && (
          <div className="space-y-1 border-t border-border px-4 py-2">
            <p className="text-xs font-medium text-muted-foreground">
              Voicemail drops
            </p>
            {config.voicemailDrops.length === 0 && (
              <p className="text-xs text-muted-foreground">
                No drops configured — add in Settings → Team dialer.
              </p>
            )}
            {config.voicemailDrops.map((d) => (
              <button
                key={d.id}
                type="button"
                className="block w-full rounded-md border border-border px-2 py-1.5 text-left text-sm hover:bg-surface-hover"
                onClick={() => {
                  void sp.leaveVoicemail(d.id);
                  setVmOpen(false);
                }}
              >
                {d.label}
              </button>
            ))}
          </div>
        )}

        {sp.showPostCall && sp.activeCall && (
          <div className="space-y-3 border-t border-border px-4 py-3">
            <p className="text-sm font-medium">Log call outcome</p>
            <div className="grid gap-2 sm:grid-cols-2">
              <label className="block text-xs">
                Purpose
                <select
                  className="mt-1 w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm"
                  value={purposeId}
                  onChange={(e) => setPurposeId(e.target.value)}
                >
                  <option value="">Select…</option>
                  {config?.purposes.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block text-xs">
                Disposition
                <select
                  className="mt-1 w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm"
                  value={dispositionId}
                  onChange={(e) => setDispositionId(e.target.value)}
                >
                  <option value="">Select…</option>
                  {config?.dispositions.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.name}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <textarea
              className="w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm"
              rows={2}
              placeholder="Notes / key takeaways"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
            {sp.activeCall.recordingUrl && (
              <audio
                controls
                className="w-full"
                src={sp.activeCall.recordingUrl}
              />
            )}
            <div className="flex justify-end gap-2">
              <button
                type="button"
                className="rounded-md border border-border px-3 py-1.5 text-sm"
                onClick={sp.dismissPostCall}
              >
                Dismiss
              </button>
              <button
                type="button"
                className="rounded-md bg-brand px-3 py-1.5 text-sm text-white"
                onClick={() =>
                  void sp.savePostCall({
                    purposeId: purposeId || undefined,
                    dispositionId: dispositionId || undefined,
                    notes: notes || undefined,
                  })
                }
              >
                Save
              </button>
            </div>
          </div>
        )}

        {(sp.error || sp.lastConnectionError) && (
          <p
            className={cn(
              "border-t border-border px-4 py-2 text-xs text-red-600",
            )}
          >
            {sp.error || sp.lastConnectionError}
          </p>
        )}
      </div>
    </div>
  );
}
